import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';
import Select from '@/components/select/select';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import {
  CreateAdminSchema,
  createAdminMutationOptions,
} from '@/features/admins/api/create-admin.api';
import { GET_LIST_ADMIN_QUERY_KEY } from '@/features/admins/api/get-list-admin.api';
import { getListRoleQueryOptions } from '@/features/roles/api/get-list-role.api';

export interface CreateAdminDialogProps extends BaseDialogProps {}

export default function CreateAdminDialog({ dialog }: CreateAdminDialogProps) {
  const { closeCreateAdminDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const getListRoleQuery = useQuery(getListRoleQueryOptions({ limit: 100 }));
  const roles = getListRoleQuery.data?.result.data ?? [];
  const roleOptions = roles.map(role => ({
    value: role.id,
    label: role.role,
  }));

  const createAdminMutation = useMutation({
    ...createAdminMutationOptions(),
    onSuccess: () => {
      toast.success('Admin berhasil ditambahkan');
      queryClient.invalidateQueries({
        queryKey: [GET_LIST_ADMIN_QUERY_KEY],
      });
      form.reset();
      closeCreateAdminDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menambahkan admin';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      password_confirmed: '',
      role_id: '',
    },
    onSubmit: async ({ value }) => {
      const parsed = CreateAdminSchema.safeParse(value);
      if (!parsed.success) {
        toast.error(
          parsed.error.issues[0]?.message ?? 'Data admin tidak valid'
        );
        return;
      }
      await createAdminMutation.mutateAsync(parsed.data);
    },
  });

  const isPending = createAdminMutation.isPending;
  const isLoadingRoles = getListRoleQuery.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeCreateAdminDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tambah Admin</DialogTitle>
          <DialogDescription>
            Lengkapi data berikut untuk menambahkan admin baru.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field name="name">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Nama Admin</Label>
                <Input
                  id={field.name}
                  placeholder="Masukkan Nama Admin"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="email">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Email</Label>
                <Input
                  id={field.name}
                  type="email"
                  placeholder="Masukkan Email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="role_id">
            {field => (
              <div className="grid gap-2">
                <Label>Role</Label>
                <Select
                  className="w-full"
                  placeholder={isLoadingRoles ? 'Memuat role...' : 'Pilih Role'}
                  options={roleOptions}
                  value={field.state.value}
                  onValueChange={value => field.handleChange(value)}
                  disabled={isLoadingRoles}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="password">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Password</Label>
                <Input
                  id={field.name}
                  type="password"
                  placeholder="Masukkan Password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="password_confirmed">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Konfirmasi Password</Label>
                <Input
                  id={field.name}
                  type="password"
                  placeholder="Masukkan Konfirmasi Password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeCreateAdminDialog}
              disabled={isPending}
            >
              Batal
            </Button>
            <form.Subscribe selector={state => [state.canSubmit]}>
              {([canSubmit]) => (
                <Button type="submit" disabled={!canSubmit || isPending}>
                  {isPending ? 'Menyimpan...' : 'Tambah'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
